import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {getPartyDisplayString} from 'parties/GetPartyDisplayString';
import {FONT_FAMILY_PROXIMA} from 'styles/FontConstants';
import {ellipsisText} from 'styles/Utilities';
import {HasAvatar} from 'ui-library/avatar/Avatar';
import AvatarText from 'ui-library/avatar/AvatarText';
import {AvatarWithName} from 'ui-library/avatar/AvatarWithName';
import {GRAY_DARKER} from 'ui-library/Colors';
import {FloatingInteraction, FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Popover from 'ui-library/floating-ui/Popover';

import {AvatarShape, AvatarSize} from './avatar/AvatarConstants';

export interface PartyListProps {
  parties: Array<HasAvatar>;
  maxParties?: number;
  popoverPlacement?: FloatingPlacement;
  useAvatar?: boolean;
  extraPopoverText?: string | null;
  popoverPrefix?: string | null;
  // The total number of relevant parties.
  // This is useful when we only fetch a subset of the relevant parties onto the client.
  totalNumberOfParties?: number;
  popoverOverride?: JSX.Element;
}

export default function PartyList({
  parties,
  maxParties,
  popoverPlacement,
  useAvatar,
  extraPopoverText,
  popoverPrefix,
  totalNumberOfParties,
  popoverOverride,
}: PartyListProps): JSX.Element {
  const partiesForDisplay = parties.slice(0, maxParties);
  const extraPartiesCount = (totalNumberOfParties ?? parties.length) - partiesForDisplay.length;

  const includePartiesInPopover = extraPartiesCount !== 0 || !useAvatar;
  const includeExtraPopoverText = extraPopoverText != null;
  const partiesText = parties.map((party) => getPartyDisplayString(party)).join(', ');
  const partiesTextWithPossiblePrefix = `${popoverPrefix ?? ''}${partiesText}`;

  const getPopoverContent = (): JSX.Element => {
    if (popoverOverride != null) {
      return popoverOverride;
    }

    return includeExtraPopoverText && includePartiesInPopover ? (
      <div>
        <p>{extraPopoverText}</p>
        <p>{partiesTextWithPossiblePrefix}</p>
      </div>
    ) : (
      <div>
        {includeExtraPopoverText && extraPopoverText}
        {includePartiesInPopover && partiesTextWithPossiblePrefix}
      </div>
    );
  };

  const popoverContent = getPopoverContent();

  return (
    <Popover
      interaction={FloatingInteraction.HOVER}
      placement={popoverPlacement}
      renderContent={() => popoverContent}
      isDisabled={!includeExtraPopoverText && !includePartiesInPopover}
    >
      {useAvatar ? (
        <PartyWithAvatarList partiesForDisplay={partiesForDisplay} extraPartiesCount={extraPartiesCount} />
      ) : (
        <EllipsizedParties parties={parties} />
      )}
    </Popover>
  );
}
PartyList.defaultProps = {
  maxParties: 2,
  tooltipPlacement: 'top',
  useAvatar: false,
};

function EllipsizedParties({parties}: {parties: Array<HasAvatar>}): JSX.Element {
  const names = parties.map((party) => party.name).join(', ');
  return <span className={css(styles.ellipsisText, styles.partyDisplayString)}>{names}</span>;
}

function PartyWithAvatarList({
  partiesForDisplay,
  extraPartiesCount,
}: {
  partiesForDisplay: Array<HasAvatar>;
  extraPartiesCount: number;
}): JSX.Element {
  return (
    <div className={css(styles.withAvatarsWrapper)}>
      {partiesForDisplay.map((party) => (
        <AvatarWithName
          key={party.name || party.handle || party.email}
          avatarInfo={party}
          size={AvatarSize.XXSMALL}
          shape={AvatarShape.CIRCLE}
          showTooltip={false}
        />
      ))}
      {extraPartiesCount > 0 && (
        <div className={css(styles.avatarWrapper)}>
          <AvatarText
            backgroundColor={GRAY_DARKER}
            size={AvatarSize.XXSMALL}
            text={`+${extraPartiesCount}`}
          />
        </div>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  avatarWrapper: {
    alignItems: 'center',
    display: 'flex',
    marginRight: 5,
  },
  ellipsisText,
  party: {
    alignItems: 'center',
    display: 'flex',
    marginRight: 14,
  },
  partyDisplayString: {
    color: GRAY_DARKER,
    fontFamily: FONT_FAMILY_PROXIMA,
    fontSize: 14,
    lineHeight: '20px',
    whiteSpace: 'nowrap',
  },
  withAvatarsWrapper: {
    display: 'inline-flex',
    flexWrap: 'wrap',
  },
});
